"use client";

import { useCallback, useRef, useState } from "react";
import { StarIcon } from "@/components/ui/StarIcon";
import { useTranslations } from "next-intl";
import Image from "@/components/CdnImage";
import { RetryImage } from "@/components/RetryImage";
import { DeferredImg, type ImageDefer } from "@/components/DeferredImage";
import { useIdleModule } from "@/lib/idlePreload";

// The lightbox is closed on arrival, and this gallery is the lander's first screen: its chunk is
// fetched once the page is idle after load (or when a finger lands on a slide), not with the page.
const loadLightbox = () => import("@/components/ImageLightbox");
const preloadLightbox = () => {
  void loadLightbox();
};

// Room-plate calibration — mirrors ProductPreview's room view: an empty
// living-room plate (2400x1792) with a ~215cm sofa → 7.35 px/cm, art hung ~25cm
// above the sofa back. Expressed as fractions of the plate so it's resolution-
// independent. On the PDP we composite a sample Vintage Poster (not the shopper's
// own art, which doesn't exist yet) so the in-room tiles read as real products.
const ROOM = { wPx: 2400, hPx: 1792, pxPerCm: 7.35, centreXpct: 50, artBottomPct: (1 - 806 / 1792) * 100 };

// Some poster renditions have a cream margin baked into the file; a mild
// scale-up crops it out so tiles read edge-to-edge. Known-bordered art gets a
// stronger zoom via its `zoom` prop.
const DEFAULT_ART_ZOOM = 1.06;

// A big slide's rendered width. PdpGalleryLead must use the same value as the gallery's slide 0, so
// the preload it emits is the request the gallery's own image reuses.
const SLIDE_SIZES = "(min-width: 1024px) 50vw, 100vw";

export type PdpGalleryImage =
  | { kind: "story"; layout: "garage" | "gift"; src: string; label: string; sub: string; headline: string; quote: string; attribution: string; rating: number }
  /** `site`: `src` is a public/ file, resized at the page's own origin (see CdnImage). */
  | { kind: "art"; src: string; label: string; sub: string; zoom?: number; site?: boolean }
  | {
      kind: "ugc";
      src: string;
      label: string;
      sub: string;
      /** Overlay card: whose car it is, when they reviewed, and the stars they gave. */
      owner: string;
      reviewed: string;
      rating: number;
    }
  /** A portrait pet piece hung framed on the room plate (PdpGalleryData's petSlide). */
  | {
      kind: "room";
      roomSrc: string;
      artSrc: string;
      widthCm: number;
      heightCm: number;
      framed: boolean;
      label: string;
      sub: string;
    };

// `defer`: every slide after the first waits for the page to load (a swipe must find it ready, but
// it must not compete with the hero), every thumbnail for the rail to show it. Inside the browser's
// lazy-load margin, `loading="lazy"` alone fetched them all during the LCP window on a phone.
function GalleryFrame({ image, sizes, priority, defer }: { image: PdpGalleryImage; sizes: string; priority?: boolean; defer?: ImageDefer }) {
  const fetchPriority = priority ? undefined : ("low" as const);
  if (image.kind === "story") {
    // Scale the complete composition so fixed spacing and minimum text sizes
    // match the main slide instead of overflowing the small thumbnail.
    if (sizes === "76px") return (
      <div aria-hidden="true" className="absolute left-0 top-0 h-[1000%] w-[1000%] origin-top-left scale-[0.1]">
        <GalleryFrame defer={defer} image={image} sizes="760px" />
      </div>
    );
    const stars = <span aria-label={`${image.rating} out of 5 stars`} className="flex gap-1 text-[#D9B980]">{Array.from({ length: image.rating }, (_, i) => <StarIcon key={i} style={{ width: '2.4cqw', height: '2.4cqw' }} className="fill-current" />)}</span>;
    if (image.layout === "garage") return (
      <div className="relative h-full overflow-hidden bg-ink text-paper" style={{ containerType: 'inline-size' }}>
        <RetryImage alt={image.label} className="object-cover" defer={defer} fill sizes={sizes} src={image.src} fetchPriority={fetchPriority} />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(145deg, rgba(26,24,21,.55), transparent 38%), linear-gradient(120deg, transparent 42%, rgba(26,24,21,.12) 55%, rgba(26,24,21,.88) 100%)' }} />
        <div className="absolute left-[5%] top-[5%] flex items-center gap-3">
          <DeferredImg alt="Vellum" defer={defer ?? "idle"} height={130} src="/assets/wordmark-primary-inverted.png" style={{ width: '19cqw', height: 'auto' }} width={509} />
        </div>
        <div className="absolute bottom-[8%] right-[5%] w-[39%]">
          <p className="mb-3 font-mono uppercase tracking-[.15em]" style={{ fontSize: 'clamp(8px, 1.5cqw, 12px)' }}>A real car. A real customer.</p>
          <blockquote className="font-display leading-[1.04] tracking-[-.035em]" style={{ fontSize: '7cqw' }}>“It turned out <em>fantastic!</em>”</blockquote>
          <div className="mt-4 border-t border-paper/40 pt-3">{stars}<p className="mt-2 leading-relaxed text-paper/85" style={{ fontSize: 'clamp(9px, 1.65cqw, 13px)' }}>{image.attribution}<br />Review excerpt</p></div>
        </div>
      </div>
    );
    return (
      <div className="relative h-full overflow-hidden bg-[#8E2D17] text-paper" style={{ containerType: 'inline-size' }}>
        <div className="absolute inset-y-0 right-0 w-[56%] bg-[#DCD3BD]">
          <RetryImage alt={image.label} className="object-contain" defer={defer} fill sizes={sizes} src={image.src} fetchPriority={fetchPriority} />
        </div>
        <div className="absolute inset-y-[6%] left-[5%] flex w-[34%] flex-col">
          <DeferredImg alt="Vellum" defer={defer ?? "idle"} height={130} src="/assets/wordmark-primary-inverted.png" style={{ width: '20cqw', height: 'auto' }} width={509} />
          <div className="my-auto py-3">
            <p className="mb-3 font-mono uppercase tracking-[.12em] text-paper/75" style={{ fontSize: 'clamp(8px, 1.5cqw, 12px)' }}>The retirement gift</p>
            <blockquote className="font-display italic leading-[.96] tracking-[-.04em]" style={{ fontSize: '9cqw' }}>“BLOWN<br />AWAY”</blockquote>
            <p className="mt-4 leading-snug" style={{ fontSize: 'clamp(10px, 2cqw, 15px)' }}>Her husband.<br />Their Bronco.<br />His retirement surprise.</p>
          </div>
          <div className="border-t border-paper/35 pt-3">{stars}<p className="mt-2 text-paper/80" style={{ fontSize: 'clamp(8px, 1.5cqw, 12px)' }}>DeAnna · Review excerpt</p></div>
        </div>
      </div>
    );
  }
  // Customer photos arrive in arbitrary aspect ratios (RetryImage resizes them
  // at the edge via CdnImage), so contain them on the paper surface instead of
  // hard-cropping to 4:3.
  if (image.kind === "ugc") {
    return (
      <RetryImage
        alt={image.label}
        className="object-contain"
        defer={defer}
        fetchPriority={fetchPriority}
        fill
        priority={priority}
        sizes={sizes}
        src={image.src}
      />
    );
  }

  if (image.kind === "art") {
    const zoom = image.zoom ?? DEFAULT_ART_ZOOM;
    return (
      <Image
        alt={image.label}
        className="object-cover"
        defer={defer}
        fetchPriority={fetchPriority}
        fill
        priority={priority}
        site={image.site}
        sizes={sizes}
        src={image.src}
        style={{ transform: `scale(${zoom})` }}
      />
    );
  }

  // Composite: sample poster overlaid on the room plate at true physical scale.
  const widthPct = ((image.widthCm * ROOM.pxPerCm) / ROOM.wPx) * 100;
  const framePct = (2.5 / image.widthCm) * 100; // float-frame border ≈ 2.5cm
  return (
    <>
      <Image alt={image.label} className="object-cover" defer={defer} fetchPriority={fetchPriority} fill priority={priority} sizes={sizes} src={image.roomSrc} />
      <div
        className="absolute overflow-hidden"
        style={{
          width: `${widthPct}%`,
          aspectRatio: `${image.widthCm} / ${image.heightCm}`,
          left: `${ROOM.centreXpct}%`,
          bottom: `${ROOM.artBottomPct}%`,
          transform: "translateX(-50%)",
          boxShadow: "0 2px 7px rgba(0,0,0,0.28)",
        }}
      >
        {image.framed ? (
          <div className="h-full w-full bg-[#0c0c0c]" style={{ padding: `${framePct}%` }}>
            <div className="relative h-full w-full overflow-hidden">
              <Image alt="" className="object-cover" defer={defer} fill sizes="40vw" src={image.artSrc} style={{ transform: `scale(${DEFAULT_ART_ZOOM})` }} />
            </div>
          </div>
        ) : (
          <Image alt="" className="object-cover ring-1 ring-black/10" defer={defer} fill sizes="40vw" src={image.artSrc} style={{ transform: `scale(${DEFAULT_ART_ZOOM})` }} />
        )}
      </div>
    </>
  );
}

/**
 * Provenance card over a customer photo: whose car it is, when they reviewed and the
 * stars they gave. Only on the big slides — at thumbnail size it would be illegible.
 * Sits on a translucent scrim so it stays readable over both bright and dark photos.
 */
function UgcOverlay({ owner, reviewed, rating }: { owner: string; reviewed: string; rating: number }) {
  return (
    <figcaption className="pointer-events-none absolute bottom-3 left-3 max-w-[85%] rounded-md bg-black/55 px-2.5 py-1.5 backdrop-blur-sm">
      <p className="truncate font-sans text-[12px] font-semibold leading-tight text-white">{owner}</p>
      <p className="mt-0.5 truncate font-sans text-[11px] leading-tight text-white/75">{reviewed}</p>
      {/* Five stars with only `rating` filled — a 4-star review must not read as 5. */}
      <span aria-hidden="true" className="mt-1 flex items-center gap-0.5">
        {Array.from({ length: 5 }, (_, i) => (
          <StarIcon
            className={`h-2.5 w-2.5 ${i < rating ? "fill-gold text-gold" : "fill-transparent text-white/40"}`}
            key={i}
            strokeWidth={1.5}
          />
        ))}
      </span>
    </figcaption>
  );
}

/**
 * The gallery's footprint while it streams in, with its first slide already painted: the bordered
 * 4:3 slide (the same boxes, classes and image props PdpGallery renders for slide 0) and an empty
 * thumbnail row (mt-2.5 + h-14 + pb-1). Nothing moves and nothing changes when the gallery lands;
 * the LCP image just starts with the document instead of ~420 KB into it.
 */
export function PdpGalleryLead({ image }: { image: PdpGalleryImage }) {
  return (
    <div className="min-w-0">
      <div className="flex overflow-hidden border border-rule bg-paper-deep paper-grain">
        <figure className="relative aspect-[4/3] w-full shrink-0 overflow-hidden">
          <GalleryFrame image={image} priority sizes={SLIDE_SIZES} />
        </figure>
      </div>
      <div aria-hidden="true" className="mt-2.5 pb-1">
        <div className="h-14" />
      </div>
    </div>
  );
}

type PdpGalleryProps = {
  images: PdpGalleryImage[];
  /** Screen-reader label for the thumbnail rail. */
  thumbnailsLabel: string;
};

/**
 * Swipeable PDP gallery: a scroll-snap track of 4:3 slides (native swipe on mobile) and a
 * thumbnail rail that stays in sync. Slides are the product shot, the Vintage Poster art, or
 * real customer photos from reviews — the latter carrying a provenance overlay. Navigation
 * only changes the visible image — no variant is chosen.
 */
export function PdpGallery({ images, thumbnailsLabel }: PdpGalleryProps) {
  const t = useTranslations("lightbox");
  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState<number | null>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const ticking = useRef(false);
  const ImageLightbox = useIdleModule(loadLightbox, lightbox !== null)?.ImageLightbox;

  // Track native swipes: derive the active slide from the snap position.
  const onScroll = useCallback(() => {
    if (ticking.current) return;
    ticking.current = true;
    requestAnimationFrame(() => {
      ticking.current = false;
      const track = trackRef.current;
      if (!track || track.clientWidth === 0) return;
      const index = Math.round(track.scrollLeft / track.clientWidth);
      setActive((prev) => (index === prev ? prev : Math.max(0, Math.min(index, images.length - 1))));
    });
  }, [images.length]);

  const goTo = useCallback((index: number) => {
    const track = trackRef.current;
    if (!track) return;
    track.scrollTo({ left: index * track.clientWidth, behavior: "smooth" });
    setActive(index);
  }, []);

  return (
    <div className="min-w-0">
      {/* Swipe track */}
      <div
        className="flex snap-x snap-mandatory overflow-x-auto border border-rule bg-paper-deep paper-grain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        onScroll={onScroll}
        ref={trackRef}
      >
        {images.map((image, index) => (
          <figure
            className="relative aspect-[4/3] w-full shrink-0 snap-center overflow-hidden"
            key={image.kind === "room" ? `${image.roomSrc}|${image.artSrc}` : image.src}
          >
            <GalleryFrame defer={index === 0 ? undefined : "idle"} image={image} priority={index === 0} sizes={SLIDE_SIZES} />
            {image.kind === "ugc" ? (
              <UgcOverlay owner={image.owner} rating={image.rating} reviewed={image.reviewed} />
            ) : null}
            {/* Tap anywhere on the slide to enlarge; a swipe still scrolls the snap track. */}
            <button
              aria-label={t("enlarge", { name: image.label })}
              className="absolute inset-0 cursor-zoom-in"
              onClick={() => setLightbox(index)}
              onPointerDown={preloadLightbox}
              type="button"
            />
          </figure>
        ))}
      </div>

      {/* Thumbnails */}
      <ul aria-label={thumbnailsLabel} className="mt-2.5 flex gap-2 overflow-x-auto pb-1">
        {images.map((image, index) => (
          <li className="shrink-0" key={image.kind === "room" ? `${image.roomSrc}|${image.artSrc}` : image.src}>
            <button
              aria-current={index === active}
              aria-label={image.label}
              className={`relative block h-14 w-[4.7rem] overflow-hidden border bg-paper-deep transition-all ${
                index === active ? "border-accent opacity-100" : "border-rule opacity-70 hover:opacity-100"
              }`}
              onClick={() => goTo(index)}
              type="button"
            >
              <GalleryFrame defer="near" image={image} sizes="76px" />
            </button>
          </li>
        ))}
      </ul>

      {lightbox !== null && ImageLightbox ? (
        <ImageLightbox
          index={lightbox}
          labels={{ close: t("close"), previous: t("previous"), next: t("next") }}
          onClose={() => setLightbox(null)}
          onIndexChange={setLightbox}
          slides={images.map((image) => ({
            alt: image.label,
            caption: image.label,
            // Room composites are layered images, not one file — re-render the frame full size.
            ...(image.kind === "room" || image.kind === "story" ? { node: <GalleryFrame image={image} sizes="100vw" /> } : { src: image.src }),
          }))}
        />
      ) : null}
    </div>
  );
}
